import { create } from 'zustand';
import { RequestStatus } from '@/shared/model/requestStatus';
import { getErrorMessage } from '@/shared/lib/errors';
import { dialogService } from '../api/dialogService';
import type { UserDialogState, DialogResource } from './types';

let templatesRevision = 0;
let broadcastsRevision = 0;
let audienceRevision = 0;
let detailsRevision = 0;

/** Подготавливает незагруженный ресурс */
const emptyResource = <T,>(): DialogResource<T> => ({
    data: null,
    status: RequestStatus.Idle,
    error: null
});

/** Хранит конфигурации, выпуски и статистику диалоговых окон */
export const useUserDialogStore = create<UserDialogState>((set) => ({
    templates: emptyResource(),
    broadcasts: emptyResource(),
    audience: emptyResource(),
    details: emptyResource(),

    loadTemplates: async () => {
        const revision = ++templatesRevision;

        set({
            templates: {
                data: null,
                status: RequestStatus.Loading,
                error: null
            }
        });

        try {
            const data = await dialogService.getTemplates();

            if (revision === templatesRevision) set({
                templates: {
                    data,
                    status: RequestStatus.Success,
                    error: null
                }
            });
        } catch (error: unknown) {
            if (revision === templatesRevision) set({
                templates: {
                    data: null,
                    status: RequestStatus.Error,
                    error: getErrorMessage(error, 'Не удалось загрузить шаблоны')
                }
            });
        }
    },

    loadBroadcasts: async (request) => {
        const revision = ++broadcastsRevision;

        set({
            broadcasts: {
                data: null,
                status: RequestStatus.Loading,
                error: null
            }
        });

        try {
            const data = await dialogService.getBroadcasts(request);

            if (revision === broadcastsRevision) set({
                broadcasts: {
                    data,
                    status: RequestStatus.Success,
                    error: null
                }
            });
        } catch (error: unknown) {
            if (revision === broadcastsRevision) set({
                broadcasts: {
                    data: null,
                    status: RequestStatus.Error,
                    error: getErrorMessage(error, 'Не удалось загрузить выпуски')
                }
            });
        }
    },

    loadAudience: async (request) => {
        const revision = ++audienceRevision;

        set({
            audience: {
                data: null,
                status: RequestStatus.Loading,
                error: null
            }
        });

        try {
            const data = await dialogService.getAudience(request);

            if (revision === audienceRevision) set({
                audience: {
                    data,
                    status: RequestStatus.Success,
                    error: null
                }
            });
        } catch (error: unknown) {
            if (revision === audienceRevision) set({
                audience: {
                    data: null,
                    status: RequestStatus.Error,
                    error: getErrorMessage(error, 'Не удалось загрузить получателей')
                }
            });
        }
    },

    loadDetails: async (id, request) => {
        const revision = ++detailsRevision;

        set({
            details: {
                data: null,
                status: RequestStatus.Loading,
                error: null
            }
        });

        try {
            const data = await dialogService.getDetails(id, request);

            if (revision === detailsRevision) set({
                details: {
                    data,
                    status: RequestStatus.Success,
                    error: null
                }
            });
        } catch (error: unknown) {
            if (revision === detailsRevision) set({
                details: {
                    data: null,
                    status: RequestStatus.Error,
                    error: getErrorMessage(error, 'Не удалось загрузить статистику')
                }
            });
        }
    },

    clearAudience: () => {
        audienceRevision++;
        set({ audience: emptyResource() });
    },
}));
